/**
 * Pay-calendar tiling from `settings/payroll.cutoffStartDays` (PRD_MonthlySalaryType
 * C2/C6/C7/C9).
 *
 * The company stores only the two cutoff START days (C7 fixes the count at
 * exactly 2 — semi-monthly, Labor Code Art. 103). Each period runs from its
 * start day to the day before the next start day, wrapping across the month
 * boundary, so `[1, 16]` reproduces the classic 1–15 / 16–end calendar and
 * `[5, 21]` yields 5–20 and 21→4-of-next-month with no special-casing. A start
 * day greater than the days in a month clamps to that month's last day (C2).
 *
 * Everything here is pure integer calendar math on 'YYYY-MM-DD' strings —
 * no Date-object timezone traps (CLAUDE.md date-boundary rule). Day serials
 * use Date.UTC, which is timezone-free.
 *
 * MIRROR: functions/src/payroll/payCutoffs.js is a verbatim CommonJS copy
 * (the server eligibility gate is authoritative and cannot import from src/).
 * Keep the two in sync — the Tier-1 suite asserts parity over the full matrix.
 */

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysInMonth = (y, m) => (m === 2 && isLeapYear(y) ? 29 : MONTH_DAYS[m - 1]);

const toYMD = (y, m, d) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const parseYMD = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return { y, m, d };
};
// Timezone-free day serial (days since epoch).
const toSerial = (y, m, d) => Date.UTC(y, m - 1, d) / 86400000;
const serialToYMD = (serial) => {
  const dt = new Date(serial * 86400000);
  return toYMD(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
};

// Realized (clamped) start serials for one calendar month, sorted.
const realizedStartsForMonth = (cutoffStartDays, y, m) => {
  const dim = daysInMonth(y, m);
  return [...cutoffStartDays]
    .map((day) => Math.min(day, dim))
    .sort((a, b) => a - b)
    .map((day) => toSerial(y, m, day));
};

const addMonths = (y, m, k) => {
  const zero = y * 12 + (m - 1) + k;
  return { y: Math.floor(zero / 12), m: (zero % 12 + 12) % 12 + 1 };
};

/**
 * Tile the official periods whose START falls within [monthsBack, monthsForward]
 * months of `aroundYMD`'s month. Each period: { start, end, isCurrent } with
 * 'YYYY-MM-DD' strings; `isCurrent` marks the period containing `aroundYMD`.
 * A cross-month period belongs to its start month (C3).
 */
export const officialPeriods = (
  cutoffStartDays,
  aroundYMD,
  { monthsBack = 2, monthsForward = 1 } = {}
) => {
  const { y, m } = parseYMD(aroundYMD);
  const todaySerial = toSerial(y, m, parseYMD(aroundYMD).d);

  // Collect starts over an extra month on each side so every listed period
  // has its "next start" available for the end computation.
  const starts = [];
  for (let k = -monthsBack - 1; k <= monthsForward + 1; k++) {
    const { y: yy, m: mm } = addMonths(y, m, k);
    starts.push(...realizedStartsForMonth(cutoffStartDays, yy, mm));
  }
  const sorted = [...new Set(starts)].sort((a, b) => a - b);

  const windowLo = realizedStartsForMonth(
    cutoffStartDays,
    addMonths(y, m, -monthsBack).y,
    addMonths(y, m, -monthsBack).m
  )[0];
  const windowHi = toSerial(
    addMonths(y, m, monthsForward).y,
    addMonths(y, m, monthsForward).m,
    daysInMonth(addMonths(y, m, monthsForward).y, addMonths(y, m, monthsForward).m)
  );

  const periods = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1] - 1;
    if (start < windowLo || start > windowHi) continue;
    periods.push({
      start: serialToYMD(start),
      end: serialToYMD(end),
      isCurrent: todaySerial >= start && todaySerial <= end,
    });
  }
  return periods;
};

/** The official period containing `todayYMD` → { start, end } (or null). */
export const currentPeriod = (cutoffStartDays, todayYMD) => {
  const hit = officialPeriods(cutoffStartDays, todayYMD, {
    monthsBack: 1,
    monthsForward: 1,
  }).find((p) => p.isCurrent);
  return hit ? { start: hit.start, end: hit.end } : null;
};

/**
 * C8 exact-match: does [startYMD, endYMD] equal one tiled official period
 * (start AND end)? Monthly employees are payable only in exact matches.
 */
export const isOfficialPeriod = (cutoffStartDays, startYMD, endYMD) =>
  officialPeriods(cutoffStartDays, startYMD, { monthsBack: 1, monthsForward: 1 })
    .some((p) => p.start === String(startYMD).slice(0, 10) && p.end === String(endYMD).slice(0, 10));

/**
 * Save-time validation (C2/C6/C7). Returns { ok: true } or { ok: false, error }.
 * - exactly two integer start days in 1..31, distinct as configured (C7);
 * - no realized collapse: the two starts must clamp to different dates in
 *   EVERY month (e.g. [28, 30] collapses in February) (C2);
 * - Art. 103: every realized period must span ≤ 16 days, checked across all
 *   12 months of a leap and a non-leap year (worst case) (C6).
 */
export const validateCutoffStartDays = (cutoffStartDays) => {
  if (!Array.isArray(cutoffStartDays) || cutoffStartDays.length !== 2) {
    return { ok: false, error: 'Exactly two cutoff start days are required (semi-monthly).' };
  }
  const [a, b] = cutoffStartDays;
  if (![a, b].every((d) => Number.isInteger(d) && d >= 1 && d <= 31)) {
    return { ok: false, error: 'Cutoff start days must be whole days between 1 and 31.' };
  }
  if (a === b) {
    return { ok: false, error: 'The two cutoff start days must be different.' };
  }
  // Sweep a leap year (2028) and a non-leap year (2025) — covers month lengths
  // 28/29/30/31 — validating REALIZED periods, not just configured numbers.
  // Collapses first (the more fundamental config error), then Art. 103 gaps.
  for (const year of [2025, 2028]) {
    for (let month = 1; month <= 12; month++) {
      const [s1, s2] = realizedStartsForMonth(cutoffStartDays, year, month);
      if (s1 === s2) {
        return {
          ok: false,
          error: `Both cutoffs fall on the same day in ${toYMD(year, month, 1).slice(0, 7)} (short month) — pick days that stay distinct in February.`,
        };
      }
    }
  }
  for (const year of [2025, 2028]) {
    for (let month = 1; month <= 12; month++) {
      const [s1, s2] = realizedStartsForMonth(cutoffStartDays, year, month);
      // Period lengths: [s1..s2-1] and [s2..next month's first start - 1].
      const next = addMonths(year, month, 1);
      const [n1] = realizedStartsForMonth(cutoffStartDays, next.y, next.m);
      const len1 = s2 - s1;
      const len2 = n1 - s2;
      if (len1 > 16 || len2 > 16) {
        return {
          ok: false,
          error: `Pay must be at most 16 days apart (Labor Code Art. 103) — this schedule creates a ${Math.max(len1, len2)}-day period in ${toYMD(year, month, 1).slice(0, 7)}.`,
        };
      }
    }
  }
  return { ok: true };
};

export const DEFAULT_CUTOFF_START_DAYS = [1, 16];
